// What every admin screen and piece shares while admin is open: the staff member, their role, the api
// bound to them, a host's products, the catalogue, and the flash at the top of the page.
//
//   const { role, api, scopes, products, flash, setFlash } = useAdmin()
//   setFlash({ text: "Done. Sam Carter is merged into ...", keep: true })   kept across 1 navigation
//   setFlash(null)
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { adminApi } from '../api.js'
import { usePath } from '../../lib/router.jsx'

const AdminContext = createContext(null)
export const useAdmin = () => useContext(AdminContext)

export function AdminProvider({ me, role, children }) {
  const api = useMemo(() => adminApi(me), [me])
  const personId = me?.who?.person_id || null
  const [scopes, setScopes] = useState([])
  const [products, setProducts] = useState({ status: 'loading', list: [] })
  const [flash, setFlashState] = useState(null)
  const path = usePath()
  const flashMeta = useRef(null)

  // a host's products (their own staff_scopes rows)
  useEffect(() => {
    let alive = true
    if (role !== 'host' || !personId) { setScopes([]); return undefined }
    api.staff_scopes(personId).then(ids => { if (alive) setScopes(ids) }, () => { if (alive) setScopes([]) })
    return () => { alive = false }
  }, [api, role, personId])

  const loadProducts = useCallback(() => {
    let alive = true
    api.products().then(
      list => { if (alive) setProducts({ status: 'ready', list }) },
      error => { if (alive) setProducts(p => ({ ...p, status: 'error', error })) },
    )
    return () => { alive = false }
  }, [api])
  useEffect(() => loadProducts(), [loadProducts])

  // a flash goes when they move to another page, unless it was set to be kept for the next 1 (a write
  // that then opens another page: "Done. ..." on the page it lands on)
  const setFlash = useCallback(next => {
    flashMeta.current = { path: window.location.pathname, survive: next?.keep ? 1 : 0 }
    setFlashState(next || null)
  }, [])
  useEffect(() => {
    const meta = flashMeta.current
    if (!flash || !meta || meta.path === path) return
    if (meta.survive > 0) flashMeta.current = { path, survive: meta.survive - 1 }
    else setFlashState(null)
  }, [path, flash])

  const value = useMemo(() => ({
    me, role, personId, api, scopes, products: products.list, productsStatus: products.status, reloadProducts: loadProducts,
    flash, setFlash,
  }), [me, role, personId, api, scopes, products, loadProducts, flash, setFlash])
  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
}
